import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { BOOK_HREF, CONTACT_HREF } from "@/data/solutions";

const GET = [
  { label: "Positionering" },
  { label: "Brandfortælling" },
  { label: "Kommunikationsstrategi" },
  { label: "AI-strategi for kommunikation", highlight: true },
  { label: "Thought Leadership" },
  { label: "Contentstrategi" },
  { label: "PR-strategi" },
  { label: "Kampagneplanlægning" },
];

const STEPS = [
  { title: "Vi forstår virksomheden", desc: "Vi lærer jeres forretning, målgrupper, ambitioner og kommunikation at kende." },
  { title: "Vi udvikler strategien", desc: "Vi definerer jeres position, budskaber og den rolle, AI skal spille i kommunikationen." },
  { title: "Vi bygger den digitale kommunikationsmotor", desc: "Vi kombinerer strategi, Aicom AI og jeres kommunikationskanaler, så idéudvikling, indholdsproduktion og kampagner bliver hurtigere og mere sammenhængende." },
  { title: "Vi udvikler løbende", desc: "Kommunikation ændrer sig konstant. Derfor justerer vi både strategi og AI-arbejdsgange, så de følger virksomhedens udvikling." },
];

export default function StrategyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Strategi</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            En kommunikationsstrategi til en <span style={{ color: "#22D3C5" }}>AI-drevet virkelighed</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 space-y-4 text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
            <p>Kommunikation er blevet langt hurtigere. AI gør det muligt at producere mere indhold end nogensinde før. Men uden en klar strategi bliver mere indhold sjældent til bedre kommunikation.</p>
            <p>Hos Aicom hjælper vi virksomheder med at skabe en strategi, hvor forretning, brand, mennesker og AI arbejder i samme retning. På den måde bliver AI ikke bare et værktøj - men en integreret del af virksomhedens kommunikation.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
            <MButton href={CONTACT_HREF} size="lg" variant="secondary" data-testid="hero-kontakt">Kontakt os</MButton>
          </motion.div>
        </div>
      </section>

      {/* Hvad er det */}
      <section className="relative z-10 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad er det?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-6">Strategi, hvor AI og mennesker trækker samme vej</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>En kommunikationsstrategi handler ikke længere kun om målgrupper, budskaber og kanaler. Den handler også om, hvordan AI kan gøre virksomheden hurtigere, skarpere og mere synlig - uden at miste den menneskelige stemme.</p>
            <p className="text-foreground">Vi hjælper jer med at bygge den digitale røde tråd mellem strategi, AI og kommunikation, så teknologien understøtter forretningen i stedet for at styre den.</p>
          </div>
        </div>
      </section>

      {/* Hvad får I? */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad får I?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">En sammenhængende strategisk værktøjskasse</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {GET.map((g, i) => (
            <motion.div key={g.label} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.35, delay: (i % 2) * 0.06 }}
              className={`flex items-center gap-3 rounded-xl border px-5 py-4 ${g.highlight ? "border-foreground/40 bg-foreground/[0.04]" : "border-border bg-card"}`}>
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${g.highlight ? "bg-foreground" : "bg-foreground"}`}>
                {g.highlight
                  ? <Sparkles className="h-3.5 w-3.5 text-background" strokeWidth={2} />
                  : <Check className="h-3.5 w-3.5 text-background" strokeWidth={2.5} />}
              </div>
              <span className={g.highlight ? "text-foreground font-medium" : "text-foreground"}>{g.label}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sådan arbejder vi */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Sådan arbejder vi</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra strategi til digital kommunikationsmotor</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="text-5xl font-display font-medium text-muted-foreground/25">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-lg font-medium mt-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
